// src/content/posts.ts

export type PostBlock =
  | { type: "p"; text: string }
  | { type: "h2"; text: string }
  | { type: "h3"; text: string }
  | { type: "ul"; items: string[] }
  | { type: "ol"; items: string[] }
  | { type: "callout"; text: string };

export type PostCategory = "getting-started" | "automation" | "adoption";

export const CATEGORY_LABELS: Record<PostCategory, string> = {
  "getting-started": "Getting started",
  automation: "Automation",
  adoption: "Adoption",
};

export interface Post {
  slug: string;
  /** H1 on the page. */
  title: string;
  /** <title> without the " | LAA Consulting" suffix — the seo() helper appends it. Keep ≤ 43 chars. */
  seoTitle: string;
  /** 120–155 characters. */
  description: string;
  category: PostCategory;
  publishedAt: string; // ISO date
  updatedAt: string; // ISO date
  /** Path of the service page this post most naturally leads to. */
  primaryService: string;
  /** Inline links inside text use [anchor](/path). Internal paths render as router Links; https:// renders as <a rel="noopener">. */
  body: PostBlock[];
}

export const POSTS: Post[] = [
  {
    slug: "how-to-implement-ai-in-a-small-business",
    title: "How to implement AI in a small business without buying a tool first",
    seoTitle: "How to Implement AI in a Small Business",
    description:
      "A practical order of operations for implementing AI in a small business: start with the work, not the tool, and know when a plain rule beats a model.",
    category: "getting-started",
    publishedAt: "2026-09-17",
    updatedAt: "2026-09-17",
    primaryService: "/services/ai-opportunity-assessment",
    body: [
      {
        type: "p",
        text: "The usual way a small business adopts AI runs like this: someone sees a demo, a subscription gets bought, and the team is told to use it. A month later, nobody's job depends on it and the subscription is still being paid. The tool was never attached to a piece of work that mattered, so there was never a way to tell whether it helped.",
      },
      {
        type: "p",
        text: "The order of operations below is the one we use. It starts with the work and arrives at the technology last, which is slower for the first week and much faster after that.",
      },
      { type: "h2", text: "Start with a task, not a technology" },
      {
        type: "p",
        text: 'Pick one task. Not a department, not a goal like "be more efficient," one task. The good candidates share four traits: it happens often, it follows a mostly stable pattern, someone in the business owns it, and everyone agrees it is annoying.',
      },
      {
        type: "p",
        text: "Most small businesses have several of these hiding in plain sight. A shared inbox where the same six questions get typed out by hand every day. Invoices re-keyed from PDFs into the accounting system. Proposals assembled from old files. Appointment rescheduling that turns into a five-message thread. Monthly reporting built from exports and tabs. The onboarding question that every new hire asks in week one.",
      },
      {
        type: "p",
        text: "If you cannot name one, that is useful information too. It usually means the work has not been looked at closely enough yet, and the next step is to watch it rather than to buy anything.",
      },
      { type: "h2", text: "Write down how it works today" },
      {
        type: "p",
        text: "Not the version in the handbook. The real version, including the workarounds. Who touches the task, in what order, in which systems, and where it sits waiting. Ask the person who does it, not the person who manages it — managers describe the process as it was designed, and operators describe it as it is.",
      },
      {
        type: "p",
        text: "This step is where most of the value of an AI project comes from, and it is the step people skip. Half the time, writing the task down reveals that two steps could be removed without any software at all.",
      },
      { type: "h2", text: 'Decide what "working" means before you build' },
      {
        type: "p",
        text: "Define three things in writing: what the output is, how you would check whether it is right, and who reviews it. The cost of being wrong decides the review step. A draft reply to a customer that a person reads before sending needs a light check. Anything that touches money, a legal document, or a customer's health or personal data needs a person in the loop every time, at least to begin with.",
      },
      {
        type: "p",
        text: "If you cannot describe what a wrong answer looks like, you are not ready to automate the task. That is not a failure; it is the assessment doing its job.",
      },
      { type: "h2", text: "Ask whether it needs AI at all" },
      {
        type: "p",
        text: 'A surprising share of "AI projects" are a reminder, a form, or an integration between two systems. If you can write the rule down completely — when this happens, do that — you want plain automation, which is cheaper, easier to test, and easier to explain. AI belongs where the input is messy: free-text emails, scanned documents, things that need to be classified or summarized or drafted, decisions that a person currently makes by reading and judging.',
      },
      {
        type: "p",
        text: "We wrote more about that distinction in [automation versus AI automation](/blog/automation-vs-ai-automation). The short version: use a rule where a rule works, and reserve the model for the part that actually needs judgment.",
      },
      { type: "h2", text: "Try the smallest version by hand" },
      {
        type: "p",
        text: "Before anyone builds anything, have one person do the task with a general-purpose AI tool, manually, for a week or two. Paste the email in, ask for a draft, edit it, send it. Upload the document, ask for the fields, check them against the original. Keep a simple tally: how long it took before, how long it takes now, and how often the output needed a real correction.",
      },
      {
        type: "p",
        text: "Those are your numbers, from your work. They are worth more than any figure a vendor quotes, and they tell you whether the task is worth automating properly at all. Keep the human in the loop throughout this stage, and keep anything sensitive out of the tool — see the policy note below.",
      },
      { type: "h2", text: "Then decide: build, buy, or stop" },
      { type: "p", text: "There are three honest outcomes, and all three are fine." },
      {
        type: "ul",
        items: [
          "Build: the manual trial saved real time, the pattern is stable, and it is worth integrating into the tools your team already uses so nobody has to copy and paste.",
          "Buy: an existing product already does exactly this inside a system you own, and configuring it is cheaper than building.",
          "Stop: the trial showed the task is too variable, too rare, or too risky, or the savings were smaller than the effort. Stopping here costs you a couple of weeks. Building first and finding out later costs a great deal more.",
        ],
      },
      { type: "h2", text: "What to keep out of the tools" },
      {
        type: "p",
        text: "Before the manual trial, write two lines and share them with the team: what may go into an AI tool, and what may not. Customer personal information, payment details, health records, anything under a confidentiality agreement, passwords, and unreleased financial information should stay out of general-purpose tools unless you have a contract and a configuration that covers it. The [U.S. Small Business Administration's guidance on AI](https://www.sba.gov/business-guide/manage-your-business/ai-small-business) makes the same point plainly: start small, have a person review outputs, and do not feed sensitive data into tools you have not vetted.",
      },
      {
        type: "p",
        text: "A longer policy can wait. Those two lines cannot. We have a [short template for a small-business AI policy](/blog/short-ai-usage-policy-for-a-small-business) if you want a starting point.",
      },
      { type: "h2", text: "Where a consultant fits, and where one does not" },
      {
        type: "p",
        text: "Plenty of small businesses can run everything above themselves, and should. The steps do not require technical skill; they require someone willing to look at the work closely and decide.",
      },
      {
        type: "p",
        text: "A consultant earns their fee in two situations. The first is when several candidate tasks compete and you want them ranked by payback before spending on any of them — that is what an [AI opportunity assessment](/services/ai-opportunity-assessment) is for. The second is when the manual trial worked and the build needs to be integrated with your existing systems, tested against real cases, and handed over documented, which is [AI implementation](/services/ai-implementation). If neither applies, keep going on your own.",
      },
      {
        type: "callout",
        text: "Not sure which task to start with? The [free AI readiness scorecard](/tools/ai-opportunity-scorecard) walks through the six areas of a small business where repeated work usually hides, and gives you a ranked read in about four minutes.",
      },
    ],
  },
  {
    slug: "which-tasks-should-a-small-business-automate-first",
    title: "Which tasks should a small business automate first?",
    seoTitle: "What to Automate First in a Small Business",
    description:
      "A simple test for choosing what a small business should automate first: frequency, stability, ownership and cost of being wrong, with examples by function.",
    category: "automation",
    publishedAt: "2026-09-17",
    updatedAt: "2026-09-17",
    primaryService: "/services/workflow-automation",
    body: [
      {
        type: "p",
        text: "The worst first automation is the most interesting one. The best first automation is the most boring one — the task everyone in the business knows is tedious, that happens the same way every time, and that nobody has owned long enough to fix.",
      },
      {
        type: "p",
        text: "Here is the test we use to choose, followed by the candidates that usually pass it, the ones to avoid, and a scoring sheet you can copy.",
      },
      { type: "h2", text: "The five-question test" },
      {
        type: "ol",
        items: [
          "Does it happen often? Daily or several times a week is ideal. Something that happens twice a quarter rarely repays the effort of automating it.",
          "Does it follow a stable pattern? If the steps are the same nine times out of ten, good. If every case is different, it is not an automation candidate yet.",
          "Does the information it needs already exist somewhere — a form, an inbox, a spreadsheet, a system? Automation moves and reshapes information; it cannot invent what nobody recorded.",
          "Does someone own it? A task with no owner has nobody to design the exception path, nobody to notice when it breaks, and nobody to say whether it is still needed.",
          "What is the cost of being wrong? Low cost means the automation can run on its own with a periodic check. High cost means a person reviews every output, which is still worth it, but changes the design.",
        ],
      },
      {
        type: "p",
        text: "A task that passes the first four is a candidate. The fifth question decides how it gets built.",
      },
      { type: "h2", text: "Candidates by function" },
      {
        type: "p",
        text: "These are the tasks that pass the test most often in the small businesses we see. Your own list will differ, but it will probably overlap.",
      },
      { type: "h3", text: "Customer communications" },
      {
        type: "ul",
        items: [
          "Contact form submissions that need to be sorted, assigned, and acknowledged before anyone follows up.",
          "Appointment confirmations, reminders, and rescheduling messages currently sent by hand.",
          "Replies to the same handful of questions that arrive every day, drafted from approved language for a person to check and send.",
        ],
      },
      { type: "h3", text: "Documents and data" },
      {
        type: "ul",
        items: [
          "Intake forms copied into two or three systems before work can start.",
          "Attachments renamed and filed by rules that live in one person's head.",
          "Exported spreadsheets cleaned up before every report.",
        ],
      },
      { type: "h3", text: "Sales and proposals" },
      {
        type: "ul",
        items: [
          "Lead details moved from a form into the sales record and a follow-up queue.",
          "Proposal sections assembled from approved language rather than from the last proposal someone can find.",
          "Reminders when a quote has gone quiet.",
        ],
      },
      { type: "h3", text: "Scheduling" },
      {
        type: "ul",
        items: [
          "Appointment requests checked against availability and business rules before a person confirms.",
          "Calendar changes that notify the right people without a manager forwarding them.",
        ],
      },
      { type: "h3", text: "Knowledge" },
      {
        type: "ul",
        items: [
          "The internal questions that every new hire asks in the first month, answered from maintained procedures.",
          "Checklists surfaced at the moment a task starts, instead of buried in a folder.",
        ],
      },
      { type: "h3", text: "Finance and admin" },
      {
        type: "ul",
        items: [
          "Bills and receipts prepared for entry into the accounting system, with missing details flagged.",
          "Monthly reporting assembled from exports, with a person writing the narrative.",
          "Approvals tracked so a stalled item has a named owner.",
        ],
      },
      { type: "h2", text: "What not to automate first" },
      {
        type: "ul",
        items: [
          "The rare exception. It is the most memorable case, which is why people bring it up, and the worst place to start.",
          "Anything that needs fresh judgment from a senior person every time.",
          "A process you are about to change. Automating it locks in the current version.",
          "A task with no owner. See question four.",
          "A broken process. Automating a bad process makes it faster and worse. Fix the process, then automate the fixed version.",
        ],
      },
      { type: "h2", text: "Rule or AI?" },
      {
        type: "p",
        text: 'Once a task passes the test, ask one more question: can you write the rule down completely? "When a form arrives with service type X, create a job in the system and email the customer template Y" is a rule, and plain automation handles it. "Read the email and work out what they actually want" is judgment, and that is where AI earns its place — usually with a person reviewing the result until you trust it. Adding AI to a task that a rule could handle makes the system slower to build, harder to test, and harder to explain. We wrote about that trade-off in [automation versus AI automation](/blog/automation-vs-ai-automation).',
      },
      { type: "h2", text: "How to run the first one" },
      {
        type: "ol",
        items: [
          "Pick one task. Resist the urge to do three.",
          "Write down the current steps, including the workarounds, by asking the person who does them.",
          "Decide what happens to the exceptions — where they go, who sees them, what they see.",
          "Build the smallest version that handles the normal path and routes everything else to a person.",
          "Watch it for a few weeks. Keep the old way available during that period.",
          "Only then choose the second task.",
        ],
      },
      { type: "h2", text: "A scoring sheet you can copy" },
      {
        type: "p",
        text: "Score each candidate from 1 to 3 on the first four questions, add them up, and note the cost of being wrong separately as low, medium, or high. The highest total with a low or medium cost is your first automation. The highest total with a high cost is a good second one, built with review built in.",
      },
      {
        type: "ul",
        items: [
          "Frequency: 1 = monthly or less, 2 = weekly, 3 = daily or more.",
          "Stability: 1 = every case different, 2 = mostly the same, 3 = the same every time.",
          "Information available: 1 = in someone's head, 2 = partly recorded, 3 = already in a system or form.",
          "Owner: 1 = nobody, 2 = informal, 3 = a named person.",
          "Cost of being wrong: low / medium / high.",
        ],
      },
      {
        type: "callout",
        text: "If the sheet produces two or three strong candidates and you want them built properly against the tools you already use, that is what [AI workflow automation](/services/workflow-automation) is. If you are not sure the repetitive work is where your best opportunity lies, the [free scorecard](/tools/ai-opportunity-scorecard) will tell you in a few minutes.",
      },
    ],
  },
  {
    slug: "is-your-business-ready-for-ai",
    title: "Is your business ready for AI? Seven signs you are, and four that mean not yet",
    seoTitle: "Is Your Small Business Ready for AI?",
    description:
      "Seven honest signs a small business is ready for AI, four that mean not yet, and what to do about each — without a readiness score that tells you nothing.",
    category: "getting-started",
    publishedAt: "2026-09-17",
    updatedAt: "2026-09-17",
    primaryService: "/tools/ai-opportunity-scorecard",
    body: [
      {
        type: "p",
        text: "Most AI readiness advice is written for companies with a data team. It talks about data maturity, governance frameworks, and platform strategy, and a ten-person business reads it and concludes that it is years away from being ready.",
      },
      {
        type: "p",
        text: "That is the wrong conclusion. For a small business, readiness has almost nothing to do with data infrastructure. It has to do with whether the work is visible, whether someone owns it, and whether it repeats. Here are the signs we actually look for.",
      },
      { type: "h2", text: "Seven signs you are ready" },
      { type: "h3", text: "1. You can describe how work moves through the business" },
      {
        type: "p",
        text: 'Not perfectly. Roughly. A customer asks for something, it goes here, then here, this person checks it, it gets recorded there. If you can narrate that for at least one part of the business, you can find where AI might help. If every explanation ends in "it depends who is in that day," start by writing it down.',
      },
      { type: "h3", text: "2. Something repeats often enough to be annoying" },
      {
        type: "p",
        text: "Annoyance is a good signal. The tasks people complain about are usually the ones that repeat, and repetition is what automation feeds on. If nobody in the business can name a tedious task, either the business is unusually well run or nobody has looked.",
      },
      { type: "h3", text: "3. Someone owns the process" },
      {
        type: "p",
        text: "Ownership does not mean a job title. It means that if the task changed tomorrow, one person would notice and would be the one to decide what to do. Automation needs that person to design the exception path and to say when the system is wrong.",
      },
      { type: "h3", text: "4. The information the task needs already exists somewhere" },
      {
        type: "p",
        text: "In an inbox, a form, a spreadsheet, a system, a document. AI can read, classify, extract, and draft from information that exists. It cannot recover a decision that was never recorded.",
      },
      { type: "h3", text: '5. You can name what "wrong" looks like' },
      {
        type: "p",
        text: 'If you can say "a wrong answer here would be an invoice coded to the wrong job" or "a wrong answer here would be a reply that promises a delivery date we cannot meet," you can design the review step. If you cannot describe a wrong answer, you cannot check for one.',
      },
      { type: "h3", text: "6. A person is available to review outputs for a while" },
      {
        type: "p",
        text: "Every AI system starts under supervision. Someone has to read the drafts, check the extracted fields, and correct the classifications for the first stretch. That takes time from a real person. If nobody can spare it, the project will be unsupervised from day one, which is how it ends up producing confident mistakes nobody catches.",
      },
      { type: "h3", text: '7. You are willing to hear "not this one"' },
      {
        type: "p",
        text: "Some tasks should not be automated, and some businesses should wait. The readiness that matters most is the willingness to be told that and to act on it. A business that has decided in advance that AI must be adopted somewhere will adopt it in the wrong place.",
      },
      { type: "h2", text: "Four signs that mean not yet" },
      { type: "h3", text: '1. The goal is "use AI" rather than a task' },
      {
        type: "p",
        text: '"We need an AI strategy" is not a project. "We re-key every invoice by hand and it takes the bookkeeper most of Tuesday" is a project. If the goal has no task attached, the next step is to find one, not to buy anything.',
      },
      { type: "h3", text: "2. Every case is different" },
      {
        type: "p",
        text: "Some work genuinely is. Bespoke consulting, complex negotiations, one-off projects. AI can assist that kind of work — drafting, summarizing, searching — but it cannot automate it, and a project that promises to will disappoint.",
      },
      { type: "h3", text: "3. Nobody agrees on the process" },
      {
        type: "p",
        text: "If two people describe the same task differently, the first job is to agree on which version is right. Automating a disputed process picks a side without anyone deciding to.",
      },
      {
        type: "h3",
        text: "4. The tool would need data you would rather not put in it, and no policy exists",
      },
      {
        type: "p",
        text: "Customer personal information, health records, payment details, anything under a confidentiality agreement. If the task needs that data and you have no written rule for what may go where, write the rule first. It takes an afternoon. We have a [short policy template](/blog/short-ai-usage-policy-for-a-small-business) to start from.",
      },
      { type: "h2", text: 'What "not yet" actually means' },
      {
        type: "p",
        text: 'In almost every case, "not yet" is a small fix rather than a long wait. Write the process down. Pick an owner. Agree on the version that is right. Write two lines about data. None of that requires technology, and all of it makes the eventual project better. Businesses that do these things in a few weeks are usually readier than larger companies that have spent a year on a platform.',
      },
      { type: "h2", text: "Readiness is per task, not per company" },
      {
        type: "p",
        text: 'The most useful shift is to stop asking "is my business ready for AI?" and start asking "is this task ready?" A business can be ready to automate invoice intake and nowhere near ready to automate proposal writing. Judge the tasks one at a time, start with the readiest, and let the others catch up.',
      },
      {
        type: "callout",
        text: "The [free AI readiness scorecard](/tools/ai-opportunity-scorecard) applies these questions across the six operating areas of a small business — customer communications, documents and data, sales, scheduling, knowledge, and finance — and returns a ranked read on where to start. It takes about four minutes, and there is no follow-up obligation.",
      },
    ],
  },
  {
    slug: "what-an-ai-opportunity-assessment-produces",
    title: "What an AI opportunity assessment actually produces",
    seoTitle: "What an AI Opportunity Assessment Produces",
    description:
      "What an AI opportunity assessment should produce: an operations map, a ranked register of impact and effort, readiness notes, a sequence — not a tool list.",
    category: "getting-started",
    publishedAt: "2026-09-17",
    updatedAt: "2026-09-17",
    primaryService: "/services/ai-opportunity-assessment",
    body: [
      {
        type: "p",
        text: "If the deliverable from an AI assessment is a slide deck listing tools, you paid for shopping. A real assessment ends in a set of decisions you can act on — with us, with someone else, or on your own. This is what the output should contain, so you can judge any proposal against it.",
      },
      { type: "h2", text: "An operations map" },
      {
        type: "p",
        text: "A written picture of how work actually moves through the parts of the business the assessment covered. Who does what, in which systems, in what order, and where things wait. It includes the steps nobody wrote down — the spreadsheet on one person's desktop, the check that happens because of something that went wrong two years ago.",
      },
      {
        type: "p",
        text: "The map is often the most valuable single item, and it has nothing to do with AI. Businesses use it for onboarding, for handovers, and for noticing steps that should simply stop.",
      },
      { type: "h2", text: "A ranked opportunity register" },
      {
        type: "p",
        text: "Every candidate the assessment found, in one place, scored on the same criteria. Ours has a row per opportunity with the function it sits in, what it currently costs in time, errors, or delay, the effort to address it, its dependencies, the risk if it goes wrong, and its rank.",
      },
      {
        type: "p",
        text: "The ranking should be by payback — what it is worth against what it costs — and not by how impressive the technology would be. The most valuable opportunity in a small business is frequently a dull one.",
      },
      { type: "h2", text: "Readiness and risk notes" },
      {
        type: "p",
        text: "For each opportunity near the top of the register: what would block it. The data is in a format nobody can read. The system has no way to exchange information. The process is disputed. The task touches personal data and there is no policy. Surfacing these before a build is the difference between a scoped project and a stalled one.",
      },
      { type: "h2", text: "Payback estimates that show their work" },
      {
        type: "p",
        text: 'A good estimate says "this task takes roughly this much time per week according to the people doing it; a working system would plausibly cut that by a range; here is what the build would cost; here is the resulting range." It uses your numbers, states its assumptions, and gives a range rather than a point.',
      },
      {
        type: "p",
        text: "Be suspicious of an assessment that hands you a precise return on investment. Precision here is a sign that the assumptions have been hidden, not that they have been resolved.",
      },
      { type: "h2", text: "A sequence" },
      {
        type: "p",
        text: 'What to do first, what to do next, and what not to do now. The "not now" list is as valuable as the "first" item. It is the list of things that looked attractive and turned out to be too variable, too rare, too risky, or too dependent on something that does not exist yet.',
      },
      { type: "h2", text: "Build-or-buy guidance" },
      {
        type: "p",
        text: "For the top items: whether an existing product inside a system you already own does the job, whether a plain automation without any AI would do, or whether something needs to be built. Tool names appear here only when the tool choice is genuinely part of the decision. A tool list without that context is a catalogue.",
      },
      { type: "h2", text: "What it should not contain" },
      {
        type: "ul",
        items: [
          "Vendor recommendations with no reasoning attached.",
          "Guaranteed outcomes. Nobody can guarantee what a system will do in your business before it exists.",
          "A proposal to build everything. If every opportunity is recommended, nothing was assessed.",
          "Timelines and prices that were not scoped with you in writing.",
        ],
      },
      { type: "h2", text: "How to use it if you do not hire the firm that wrote it" },
      {
        type: "p",
        text: "The register and the map should stand on their own. Take the first-ranked item, its readiness notes, and its build-or-buy call to any implementation partner — or to your own team — and ask for a fixed quote against that scope. If an assessment cannot be used that way, it was a sales document rather than advice.",
      },
      { type: "h2", text: "The self-serve version and the real thing" },
      {
        type: "p",
        text: "Our [free scorecard](/tools/ai-opportunity-scorecard) covers the same six operating areas and gives a quick ranked read in a few minutes. It is honestly useful for deciding whether the question is worth pursuing. The full [AI opportunity assessment](/services/ai-opportunity-assessment) goes inside the business: interviews with the people doing the work, walkthroughs of the systems, and the register above — with enough detail to make a decision and defend it.",
      },
      {
        type: "callout",
        text: "Whichever route you take, insist on the register. A ranked list of opportunities with impact, effort, and risk beside each one is the thing that lets you decide. Everything else is supporting material.",
      },
    ],
  },
  {
    slug: "automation-vs-ai-automation",
    title: "Automation vs AI automation: when a rule is enough",
    seoTitle: "Automation vs AI Automation",
    description:
      "The difference between plain automation and AI automation, a test for which one a task needs, and why adding AI where a rule would do makes things worse.",
    category: "automation",
    publishedAt: "2026-09-17",
    updatedAt: "2026-09-17",
    primaryService: "/services/workflow-automation",
    body: [
      {
        type: "p",
        text: "A lot of what is sold as AI automation is plumbing with a model glued to the side. That is not always wrong — sometimes the model is the useful part — but it is worth knowing which is which before you pay for either. The distinction is simple, and it decides how a system gets built, tested, and maintained.",
      },
      { type: "h2", text: "Plain automation: when this happens, do that" },
      {
        type: "p",
        text: "Plain automation follows rules. A form is submitted, so a record is created and a confirmation goes out. An invoice is approved, so it moves to the payment queue. It is a weekday at eight in the morning, so the daily job list is assembled from the calendar. The rule can be written down completely, and the system does exactly the same thing every time.",
      },
      {
        type: "p",
        text: "This kind of automation is cheap, predictable, easy to test, and easy to explain to the person whose job it changes. Most of the repetitive work in a small business — the copying, the reminding, the routing, the filing — is this kind.",
      },
      { type: "h2", text: "AI-assisted automation: when the input is messy" },
      {
        type: "p",
        text: "AI earns its place where the input is unstructured and a person currently reads and judges. An email that could be a complaint, a question, or a sales inquiry. A scanned document where the fields are in different places on every page. A pile of call notes that need to become a summary. A first draft of a reply, a proposal, or a report.",
      },
      {
        type: "p",
        text: "Here the system is not following a rule; it is making a judgment that a person would otherwise make, and it will sometimes make it wrong. That is why AI-assisted steps usually need a review point, at least until the error rate is known.",
      },
      { type: "h2", text: "The test" },
      {
        type: "p",
        text: 'Try to write the rule down completely. If you can — every condition, every outcome, no exceptions that depend on reading something — you want plain automation. If the rule keeps needing words like "usually," "depends," or "you can tell when," the task involves judgment, and that is where a model belongs.',
      },
      {
        type: "p",
        text: "Most real tasks are a mix, and the useful skill is separating the two parts rather than choosing one label for the whole thing.",
      },
      { type: "h2", text: "Why AI where a rule would do is worse" },
      {
        type: "ul",
        items: [
          "It is not deterministic. The same input can produce a slightly different output on a different day, which is fine for a draft and unacceptable for a routing decision.",
          "It costs more to run and more to maintain.",
          "It is harder to test. A rule is tested with cases; a model is tested with an evaluation set and a tolerance, which is more work.",
          'It is harder to explain. "The system routed it there because the form said X" is an answer. "The model decided" is not.',
        ],
      },
      { type: "h2", text: "Why a rule where judgment is needed is also worse" },
      {
        type: "p",
        text: "The opposite mistake is just as common. A rule-based system that tries to classify free-text email by keyword will be brittle, exceptions will pile up, and within a month people will route around it. When the input genuinely needs reading, forcing it through a rule produces an automation that everyone quietly ignores.",
      },
      { type: "h2", text: "The pattern that usually works: rules around, AI in the middle" },
      {
        type: "p",
        text: "Take an inbox that receives service requests. A plain rule receives the message and creates a record. A model reads the message, classifies it, and extracts the details a person would otherwise type. Plain rules then route it by type and urgency and send the acknowledgement. A person reviews anything the model flagged as uncertain or anything classified as high-risk before it moves on.",
      },
      {
        type: "p",
        text: "The model does the one part that needs reading. Everything around it is deterministic, testable, and cheap. That structure is what most good AI workflow automation looks like once you take the marketing off it.",
      },
      { type: "h2", text: "Testing each kind" },
      {
        type: "p",
        text: "Rules are tested with cases: here is the input, here is the required output, run them all, they must all pass. AI steps are tested with an evaluation set: a collection of real inputs with known-good answers, run through the model, with the results compared and the error rate recorded. When the underlying model changes — and it will — you re-run the set and see whether anything regressed, instead of finding out from a customer.",
      },
      {
        type: "p",
        text: "If a vendor cannot describe the evaluation set, they have not tested the AI part.",
      },
      { type: "h2", text: "What to ask a vendor" },
      {
        type: "ul",
        items: [
          "Which steps in this are rules, and which use a model?",
          "For the model steps, what does a wrong answer look like, and who catches it?",
          "What is in the evaluation set, and can we see the results?",
          "What happens when the model provider changes the model?",
          "Could any of the model steps be a rule instead? Why not?",
        ],
      },
      {
        type: "callout",
        text: "Choosing which tasks to automate first is its own question — we wrote a [scoring sheet for it](/blog/which-tasks-should-a-small-business-automate-first). If you have a workflow in mind and want it built with the rules-around, model-in-the-middle structure above, that is [AI workflow automation](/services/workflow-automation).",
      },
    ],
  },
  {
    slug: "why-ai-projects-die-in-week-three",
    title: "Why AI projects die in week three, and what adoption work actually looks like",
    seoTitle: "Why AI Projects Die After Launch",
    description:
      "AI projects rarely fail technically. They fail when the team quietly goes back to the old way. What adoption work in a small business actually involves.",
    category: "adoption",
    publishedAt: "2026-09-17",
    updatedAt: "2026-09-17",
    primaryService: "/services/ai-enablement",
    body: [
      {
        type: "p",
        text: "The failure is quiet. Nobody announces that they have stopped using the new system. Usage just decays, and by the time anyone looks, the old process is back and the budget is spent. The system may work exactly as designed. It failed anyway, because the habits, ownership, and rules around it were never changed.",
      },
      { type: "h2", text: "What week three looks like" },
      {
        type: "p",
        text: "It looks like this. One person reverts because the system got something wrong once, and nobody told them that was expected in the first month. Another was never trained properly and is faster the old way. A manager, under pressure, asks for the old spreadsheet because that is the format they know, and the team obliges — so the old process is being maintained alongside the new one. Within a quarter the new system is a tab nobody opens.",
      },
      {
        type: "p",
        text: "None of those people did anything unreasonable. Each was responding to the incentives in front of them.",
      },
      { type: "h2", text: "Why training alone does not hold" },
      {
        type: "p",
        text: "A one-hour demo teaches people where the buttons are. It does not change what their manager asks for, what they are measured on, who owns the process now, or what to do when the system is wrong. Those four things decide whether the new way survives contact with a busy Tuesday, and a demo touches none of them.",
      },
      { type: "h2", text: "The four things that have to line up" },
      {
        type: "ul",
        items: [
          "An owner. One named person who is responsible for the process as it now runs — not the tool, the process. If the old spreadsheet had an owner and the new system does not, the spreadsheet wins.",
          "The workflow. The new system has to be at least as fast as the old way for the person using it, or it has to be the only way. A new step that is slower and optional is a step nobody takes.",
          "Rules. What may go into the tool, what must be reviewed before it leaves the business, and what to do when the output is wrong. Short, written, and specific to the task.",
          "Measurement. Actual usage, from the system, checked regularly enough that decay is visible while it can still be fixed.",
        ],
      },
      { type: "h2", text: "What adoption work actually is" },
      {
        type: "p",
        text: "It starts with finding out why people are not using the thing — by asking them, not by assuming. The reasons are usually specific and usually fixable: it was slower, it was wrong once, nobody explained the exceptions, the manager wanted the old format.",
      },
      {
        type: "p",
        text: "Then it is role-specific training built around each person's real tasks, with their own examples, including examples of the system being wrong so they know what wrong looks like. It is a short written policy. It is a review step placed where the cost of an error is high. It is a way to see usage, and a follow-up a few weeks after go-live, because that is when the real problems surface.",
      },
      {
        type: "p",
        text: "It is, in other words, ordinary operational work. It is not a change-management framework, and a small business does not need one.",
      },
      { type: "h2", text: "The manager problem" },
      {
        type: "p",
        text: "The single most common cause of a shadow process is a manager who keeps asking for the old output. Not from malice; from habit and from pressure. Adoption work has to include that person. If the report now comes from the new system, the manager has to accept it in that form, and someone has to tell them so.",
      },
      { type: "h2", text: "The question people are actually asking" },
      {
        type: "p",
        text: "Behind a lot of slow adoption is a question nobody has answered out loud: is this going to replace me? People do not trust vague reassurance, and they are right not to. What they need to know is what is changing, what is not, how their work will be judged now, and where their judgment still matters. If leadership is in fact planning to reduce headcount, that has to be said plainly, because it changes the work and people will find out anyway.",
      },
      { type: "h2", text: "When the problem is the software" },
      {
        type: "p",
        text: "Sometimes the diagnosis comes back and the answer is that the system is not good enough. It is slower than the old way, or wrong too often, or built for a process that does not match how the work happens. Training a team to tolerate a bad system is not adoption work; it is a cover-up. The right move is to fix the build, shrink the rollout, or retire the tool.",
      },
      { type: "h2", text: "How to tell whether adoption is real" },
      {
        type: "ul",
        items: [
          "Usage data from the system, not self-report.",
          "Correct use — people using it for what it is for, with the review step actually happening.",
          "No shadow process. The old spreadsheet is gone, not merely discouraged.",
          "Exceptions flowing to the right person and getting handled.",
        ],
      },
      {
        type: "callout",
        text: "If you have built or bought something and usage is low, that is exactly what [AI training and adoption](/services/ai-enablement) is for — including the honest finding, when it applies, that the problem is the system rather than the people. A [short AI usage policy](/blog/short-ai-usage-policy-for-a-small-business) is often the first fix.",
      },
    ],
  },
  {
    slug: "short-ai-usage-policy-for-a-small-business",
    title: "A short AI usage policy for a small business, and why yours should be short",
    seoTitle: "AI Usage Policy for a Small Business",
    description:
      "A one-page AI usage policy a small business can actually follow: what may go into AI tools, what must stay out, which outputs need review, and who decides.",
    category: "adoption",
    publishedAt: "2026-09-17",
    updatedAt: "2026-09-17",
    primaryService: "/services/ai-enablement",
    body: [
      {
        type: "p",
        text: "A policy nobody reads is not control. Most of the AI policies in circulation are several pages of principles that no employee will consult at four in the afternoon when they are deciding whether to paste a customer's email into a chatbot. A small business needs one page, written for that moment.",
      },
      { type: "h2", text: "What the policy is for" },
      {
        type: "p",
        text: "Its job is to make a handful of decisions once, so that nobody in the business has to make them alone under time pressure. Which tools may be used. What must never go into them. Which outputs a person must check before they leave the business. Who decides when something new comes up. That is the whole job.",
      },
      { type: "h2", text: "The five sections" },
      { type: "h3", text: "1. What you may use AI tools for" },
      {
        type: "p",
        text: "Say it positively and specifically. Drafting replies, summarizing documents, first drafts of proposals, cleaning up notes, answering internal questions from approved material. A list people can recognize their own work in.",
      },
      { type: "h3", text: "2. What must never go into an AI tool" },
      {
        type: "p",
        text: "This is the section that matters most, and it should be a plain list: customer personal information, payment card details, health information, anything covered by a confidentiality agreement, passwords and credentials, unreleased financial information, and anything you would not want to see on the front page. If a task needs that data, it needs an approved tool with a contract that covers it — not a general-purpose one.",
      },
      { type: "h3", text: "3. Which outputs need a human review before they leave the business" },
      {
        type: "p",
        text: "Anything sent to a customer. Anything with legal, financial, or employment consequences. Anything published. The rule is simple: an AI draft is a draft until a named person has read it.",
      },
      { type: "h3", text: "4. Which tools are approved, and who approves new ones" },
      {
        type: "p",
        text: "Name the tools people may use today and one person who says yes or no to additions. Without this, everyone picks their own, and section two becomes unenforceable.",
      },
      { type: "h3", text: "5. Where to ask" },
      {
        type: "p",
        text: "One name. If someone is unsure, they ask this person rather than guessing. That single line prevents more problems than the rest of the document.",
      },
      { type: "h2", text: "A draft you can adapt" },
      {
        type: "p",
        text: "The text below is a starting point, not legal advice. Adapt it to your business and have your attorney review it before you adopt it, particularly the section on data.",
      },
      {
        type: "callout",
        text: "AI tools at [Company]. You may use approved AI tools to draft replies, summarize documents, prepare first drafts, tidy notes, and answer internal questions from our own procedures. Never put the following into any AI tool: customer names with contact or account details, payment card or bank information, health information, anything covered by a confidentiality agreement, passwords, or unreleased financial figures. If a task needs that information, ask [Name] before proceeding. Anything an AI tool helps you write must be read by you before it is sent, and by [Name or role] before it goes to a customer, into a contract, or into a public post. Approved tools: [list]. To use a new tool, ask [Name]. If you are not sure whether something is allowed, ask [Name] — that is always the right answer.",
      },
      { type: "h2", text: "What to leave out" },
      {
        type: "ul",
        items: [
          "Long statements of principle. They add length and remove nothing.",
          "Tool-by-tool feature rules. They are out of date the week you write them.",
          "Anything you cannot enforce. A rule that will be broken by lunchtime teaches people that the rest of the policy is optional too.",
          "Threats. The policy is there to help people decide, not to catch them out.",
        ],
      },
      { type: "h2", text: "How to roll it out" },
      {
        type: "p",
        text: "Read it aloud in a team meeting. Give two or three examples from your own business of what is fine and what is not. Ask what people are already using — you will learn something. Put the page where the work happens, not in a folder. Revisit it after a few months, when you know what actually came up.",
      },
      { type: "h2", text: "Where the policy fits" },
      {
        type: "p",
        text: "A policy on its own does not make a team use a new system well. It is one of the four things that have to line up — owner, workflow, rules, measurement — and we wrote about the other three in [why AI projects die in week three](/blog/why-ai-projects-die-in-week-three). But it is the one you can write this afternoon.",
      },
      {
        type: "callout",
        text: "If you want the policy written for your specific tools and roles, alongside the training and follow-up that make it stick, that is part of [AI training and adoption](/services/ai-enablement).",
      },
    ],
  },
  {
    slug: "ai-consulting-in-las-vegas-what-to-expect",
    title: "AI consulting in Las Vegas: what to expect, and what to ask before you hire anyone",
    seoTitle: "AI Consulting in Las Vegas: What to Expect",
    description:
      "What AI consulting looks like for a Las Vegas small business, what a first call should cover, and eight questions to ask any consultant before you sign.",
    category: "getting-started",
    publishedAt: "2026-09-17",
    updatedAt: "2026-09-17",
    primaryService: "/book",
    body: [
      {
        type: "p",
        text: "We work from downtown Las Vegas, and most of the businesses we talk to here — trades, clinics, professional services, hospitality suppliers, property teams — have the same operational shape as small businesses anywhere: a lot of repeated work, a few people who know how everything really runs, and not much time to step back and look at it. This is what AI consulting should look like for a business like that, and what to ask before hiring anyone, including us.",
      },
      { type: "h2", text: "What AI consulting is, and what it is not" },
      {
        type: "p",
        text: "It is not a software reseller. A consultant whose recommendation is always the product they are licensed to sell is a salesperson, which is a fine job but a different one. It is not a development shop that builds whatever you describe — that is useful once you know what to build, and unhelpful before. And it is not a strategy deck. A deck that says AI is important and lists opportunities by department does not change a single day of anyone's work.",
      },
      {
        type: "p",
        text: "What it is: someone who looks at how your business actually operates, finds the places where repeated work or slow handoffs are costing you, tells you which of them AI or plain automation would help with and which it would not, and then — if you want — builds it, tests it against your real work, and makes sure your team uses it.",
      },
      { type: "h2", text: "What a first call should cover" },
      {
        type: "p",
        text: "Your operations, not their slides. A good first call is mostly questions: what does the business do, where does the work pile up, who does it, what systems does it live in, what has been tried. By the end you should have heard a clear opinion about whether there is something worth pursuing, and if so what the next step is.",
      },
      {
        type: "p",
        text: 'What should follow is either a written scope with a fixed quote, or a plain "this is not worth your money yet, and here is what to do first." If the follow-up is a proposal to do everything, be careful.',
      },
      { type: "h2", text: "Eight questions to ask any consultant" },
      {
        type: "ol",
        items: [
          "What will you tell me if AI is not worth it for my business? Listen for a specific answer. The right one is that they will say so and tell you what to do instead.",
          "Who owns what you build? The answer should be you — code, configuration, documentation, and account access.",
          "Will it work inside the tools we already use? Replacing working software is rarely the cheapest route to anything.",
          "How will we know it is working? Ask what the test set is. If they cannot describe testing against real cases from your business, the system has not been tested.",
          "What happens when it is wrong? There should be a review step sized to the cost of the error, and a person who owns exceptions.",
          "What is the fixed price, and what changes it? An open-ended hourly engagement for a build is a risk you are carrying, not them.",
          "Who trains my team, and what happens in month three? Adoption is where projects fail. If the plan ends at launch, so will the usage.",
          "Can you show me a scope document from a past engagement, with the client's details removed? The shape of it tells you how they work.",
        ],
      },
      { type: "h2", text: "In person or remote" },
      {
        type: "p",
        text: "The part of the work that benefits from being in the room is the operations walkthrough — watching how a task is actually done, which is different from how it is described. For businesses in Las Vegas, Henderson, North Las Vegas, and Summerlin, we do that on site. The build, testing, and most of the training work well remotely, which is how we work with businesses elsewhere in Nevada and across the country.",
      },
      { type: "h2", text: "Red flags" },
      {
        type: "ul",
        items: [
          "Guaranteed returns. Nobody can guarantee what a system will do in your business before it exists.",
          "A proposal that starts with a tool rather than a task.",
          "No mention of testing, review steps, or exceptions.",
          "A plan with no handover — you are dependent on them after launch.",
          "A recommendation to build everything at once.",
        ],
      },
      { type: "h2", text: "What it costs" },
      {
        type: "p",
        text: "Our own work is fixed-quote against a written scope agreed before anything starts, so the price is known before you commit and scope changes are re-quoted rather than absorbed quietly. Whoever you talk to, ask for the same.",
      },
      {
        type: "callout",
        text: "The first conversation is free, twenty minutes, and ends with an honest answer about whether AI is worth pursuing in your business — including when it is not. [Book a call](/book), or [send a note](/contact) and we will reply within a business day.",
      },
    ],
  },
];

/** Posts newest first. */
export function getPosts(): Post[] {
  return [...POSTS].sort((a, b) => (a.publishedAt < b.publishedAt ? 1 : -1));
}

export function getPost(slug: string): Post | undefined {
  return POSTS.find((p) => p.slug === slug);
}

export function getPostsByCategory(category: PostCategory): Post[] {
  return getPosts().filter((p) => p.category === category);
}

/** Rough reading time from body text, at 220 words per minute. */
export function readingMinutes(post: Post): number {
  const words = post.body
    .map((b) => ("text" in b ? b.text : b.items.join(" ")))
    .join(" ")
    .split(/\s+/).length;
  return Math.max(1, Math.round(words / 220));
}
